/**
 * Per-client OpenTok quality, reported to the health dashboard over the meeting
 * socket. This data only exists inside each participant's browser - the Vonage
 * server APIs do not expose it - so a dashboard opened in another tab has to ask
 * the clients for it.
 *
 * Field names mirror @opentok/client 2.34.2 so they stay greppable against the
 * SDK docs. `networkCondition`, `networkConditionReason` and
 * `networkDegradationSource` were added in 2.34 and are absent in 2.33.
 */

/** @see OT.NetworkCondition */
export type NetworkCondition = 'unknown' | 'critical' | 'warning' | 'fair' | 'good' | 'excellent';

/** @see OT.NetworkConditionReason */
export type NetworkConditionReason = 'none' | 'unknown' | 'bandwidth' | 'packetLoss';

/**
 * Which side of the link is responsible for a degradation.
 * @see OT.NetworkDegradationSource
 */
export type NetworkDegradationSource = 'none' | 'bothOrUnclear' | 'local' | 'remote';

/** @see OT.CpuPerformanceState */
export type CpuPerformanceState = 'nominal' | 'fair' | 'serious' | 'critical';

export const NETWORK_CONDITION_ORDER: NetworkCondition[] = [
	'critical',
	'warning',
	'fair',
	'good',
	'excellent',
	'unknown',
];

export interface TransportHealth {
	networkCondition: NetworkCondition;
	networkConditionReason: NetworkConditionReason;
	/** bits per second, as estimated by the SDK */
	connectionEstimatedBandwidth: number;
}

/** This client's own outbound stream. */
export interface PublisherHealth {
	streamId?: string;
	transport?: TransportHealth;
	videoBitrate: number;
	frameRate: number;
	packetsLost: number;
	packetsSent: number;
	/** Simulcast layers the publisher is actually sending. */
	layers?: {
		width: number;
		height: number;
		encodedFrameRate: number;
		qualityLimitationReason?: string;
	}[];
}

/** One remote stream as this client is receiving it. */
export interface SubscriberHealth {
	streamId: string;
	/** OpenTok stream name, which greenroom sets to `<gr_user>,<guid>`. */
	streamName?: string;
	transport?: TransportHealth;
	/** The publisher's own transport, relayed via publishSenderStats. */
	remotePublisherTransport?: TransportHealth;
	networkDegradationSource?: NetworkDegradationSource;
	width: number;
	height: number;
	frameRate: number;
	bitrate: number;
	packetsLost: number;
	/**
	 * Cumulative count of user-visible video freezes. Unlike instantaneous
	 * bitrate, these persist after the glitch ends, so a producer opening the
	 * dashboard late still sees that something went wrong.
	 */
	freezeCount?: number;
	totalFreezesDuration?: number;
	/** MOS 1-5, from the qualityScoreChanged event rather than getStats. */
	audioQualityScore?: number;
	videoQualityScore?: number;
}

export interface ClientOpentokHealth {
	/** Identifies which participant this report came from. */
	grUser: number | null;
	name: string;
	isProducer: boolean;
	webrtcID?: string | number;
	/** epoch ms the client took the snapshot */
	collectedAt: number;
	sessionId?: string;
	/** Machine-level pressure, from the session's cpuPerformanceChanged event. */
	cpuPerformanceState?: CpuPerformanceState;
	publishers: PublisherHealth[];
	subscribers: SubscriberHealth[];
}

/** Socket message types for the on-demand health stat exchange. */
export enum HealthStatsMessage {
	/** Dashboard -> clients: send me your current stats once. */
	Request = 'health-stats-request',
	/** Client -> dashboard: here they are. Also pushed on a degradation event. */
	Report = 'health-stats-report',
}

/** Worst-first: picks the condition a summary should show for a set of links. */
export function worstNetworkCondition(
	conditions: (NetworkCondition | undefined)[],
): NetworkCondition {
	const present = conditions.filter((c): c is NetworkCondition => !!c && c !== 'unknown');
	if (!present.length) return 'unknown';

	return present.reduce((worst, condition) => (
		NETWORK_CONDITION_ORDER.indexOf(condition) < NETWORK_CONDITION_ORDER.indexOf(worst)
			? condition
			: worst
	), present[0]);
}
